import React, { ReactElement } from "react";
import DayPickerInputRaw from "react-day-picker/DayPickerInput";
const DayPickerInput = fixLibImport(DayPickerInputRaw);
import "react-day-picker/lib/style.css";
import { DateUtils, NavbarElementProps } from "react-day-picker";
import styled, { css } from "styled-components";
import { format as dateFnsFormat, parse, isValid } from "date-fns";
import { scroller, Element as ScrollElement } from "react-scroll";

import { SCROLL_ELEMENT_OFFSET } from "@config/consts";

import { AVAILABLE_LANGS } from "@typings/globals";

import { rem, rgba } from "@ui/helpers";
import { Margins, TestWrapper } from "@ui/Atoms";
import { inputStyles } from "@ui/Atoms/Form/Input/Input";
import { labelStyles } from "@ui/Atoms/Form/Label/Label";
import { FormikInputWithError } from "@ui/Molecules/FormikInputWithError";
import { Dropdown, DropdownItem } from "@ui/Molecules/Dropdown";
import { CaretLeft } from "@ui/Assets/Symbolicons/CaretLeft";
import { CaretRight } from "@ui/Assets/Symbolicons/CaretRight";
import { CalendarDaily } from "@ui/Assets/Symbolicons/CalendarDaily";

import { fixLibImport } from "@services/LibImportFix";
import {
  getWeekdaysShortByLang,
  getMonthsByLang,
  DATE_FORMAT,
  DAY_PICKER_VALID_DATE_REGEX,
} from "@services/DateFormatter";
import { numberRange } from "@services/NumberRange";

import { useBreakpoint } from "@hooks/useBreakpoint";
import { forceBlur } from "@hooks/useActiveElement";

const DAY_PICKER_SCROLL_ELEMENT = "day-picker-input";

const resolveOutputValue = (
  day: string | null | Date,
  returnRawDate = false,
): string | null | Date => {
  if (day && typeof day !== "string" && !returnRawDate) {
    const formattedDayPickerInput = isValid(day)
      ? dateFnsFormat(day, DATE_FORMAT.DAY_PICKER_INPUT)
      : String(day);

    return formattedDayPickerInput;
  }

  return day;
};

const dimensions = {
  day: rem(40),
  wrapperMobile: rem(288),
  wrapper: rem(320),
  borderGutter: rem(8),
};

export enum DAY_PICKER_THEME_VARIANT {
  CLIENT = "Client",
  MAID = "Maid",
}

interface WrapperProps {
  isDisabled?: boolean;
  variant: DAY_PICKER_THEME_VARIANT;
}

interface RenderCaptionElement {
  date: Date;
  onChange: (date: Date) => void;
  disablePast?: boolean;
}

interface Props {
  variant: DAY_PICKER_THEME_VARIANT;
  defaultValue: string; // should be in format DATE_FORMAT.DAY_PICKER_INPUT
  inputLabel: string;
  inputName: string;
  isDisabled?: boolean;
  onDayChange: (date: string | null | Date) => void;
  onInit?: (date: string | null | Date) => void;
  lang: AVAILABLE_LANGS;
  dateFormatText: string;
  disablePast?: boolean;
  disableSpecificDate?: Date;
  disableBeforeDate?: Date;
  disableWeekends?: boolean;
  disableSundays?: boolean;
  testIds?: {
    datePicker?: string;
    monthDropdown?: string;
    yearDropdown?: string;
    valueInput?: string;
  };
  errorTestId?: string;
  returnRawDate?: boolean;
  readonlyInput?: boolean;
  reverseYears?: boolean;
  hideIcon?: boolean;
  autoScroll?: boolean;
}

const DayPicker = (props: Props): ReactElement => {
  const { mobile, tablet } = useBreakpoint("v1");
  const isSmallScreen = mobile || tablet;
  const [dropdownMonth, setDropdownMonth] = React.useState<number>(
    props.defaultValue
      ? new Date(
          parse(props.defaultValue, DATE_FORMAT.DAY_PICKER_INPUT, new Date()),
        ).getMonth()
      : new Date().getMonth(),
  );
  const [dropdownYear, setDropdownYear] = React.useState<number>(
    props.defaultValue
      ? new Date(
          parse(props.defaultValue, DATE_FORMAT.DAY_PICKER_INPUT, new Date()),
        ).getFullYear()
      : new Date().getFullYear(),
  );
  const [pickerMonth, setPickerMonth] = React.useState<Date>();
  const [selectedDay, setSelectedDay] = React.useState<Date>();
  const [userInput, setUserInput] = React.useState<string>(props.defaultValue);
  const [didModifyDay, setDidModifyDay] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (props.onInit) {
      props.onInit(
        resolveOutputValue(
          parse(props.defaultValue, DATE_FORMAT.DAY_PICKER_INPUT, new Date()),
          props.returnRawDate,
        ),
      );
    }
  }, []);

  const renderCaptionElement = ({
    date,
    onChange,
    disablePast,
  }: RenderCaptionElement) => {
    const currentYear = new Date().getFullYear();
    const months = getMonthsByLang(props.lang);
    const years = numberRange({
      start: !disablePast ? currentYear - 120 : currentYear,
      end: !disablePast ? currentYear : currentYear + 10,
      step: 1,
    });

    props.reverseYears && years.reverse();

    const handleMonthYearChange = ({
      month,
      year,
    }: {
      month?: number;
      year?: number;
    }) => {
      onChange(
        new Date(
          year || dropdownYear,
          month === undefined ? dropdownMonth : month,
        ),
      );
    };

    return (
      <DayPickerCaption role="heading">
        <DropdownsWrapper>
          <Margins xs={[null, null, null, "base"]}>
            <Dropdown
              buttonTestId={props.testIds?.monthDropdown}
              currentState={months[date.getMonth()]}
              alignment={isSmallScreen ? "left" : "right"}
              dropdownMaxHeight={isSmallScreen ? rem(280) : null}
              smallPaddings
            >
              {" "}
              {months.map((month, idx) => (
                <TestWrapper
                  key={month}
                  testId={
                    props.testIds?.monthDropdown
                      ? `${props.testIds.monthDropdown}-month-${idx}`
                      : undefined
                  }
                >
                  <DropdownItem
                    onClick={e => {
                      e.preventDefault();
                      setDropdownMonth(idx);
                      handleMonthYearChange({ month: idx });
                    }}
                  >
                    {month}
                  </DropdownItem>
                </TestWrapper>
              ))}
            </Dropdown>
          </Margins>
          <Dropdown
            buttonTestId={props.testIds?.yearDropdown}
            currentState={
              years.includes(date.getFullYear())
                ? date.getFullYear()
                : new Date().getFullYear()
            }
            dropdownMaxHeight={isSmallScreen ? rem(280) : null}
            smallPaddings
          >
            {years.map((year, idx) => (
              <TestWrapper
                key={year}
                testId={
                  props.testIds?.yearDropdown
                    ? `${props.testIds.yearDropdown}-year-${idx}`
                    : undefined
                }
              >
                <DropdownItem
                  onClick={e => {
                    e.preventDefault();
                    setDropdownYear(year);
                    handleMonthYearChange({ year });
                  }}
                >
                  {year}
                </DropdownItem>
              </TestWrapper>
            ))}
          </Dropdown>
        </DropdownsWrapper>
      </DayPickerCaption>
    );
  };

  return (
    <TestWrapper testId={props.testIds?.datePicker}>
      <DayPickerInputWrapper
        isDisabled={props.isDisabled}
        variant={props.variant}
      >
        <DayPickerLabel>{props.inputLabel}</DayPickerLabel>
        <ScrollElement name={DAY_PICKER_SCROLL_ELEMENT} />
        <DayPickerInput
          ref={node => {
            const input = node?.getInput() as HTMLInputElement | null;
            if (!input || !props.testIds?.valueInput) return;

            input.setAttribute("data-testid", props.testIds?.valueInput);
          }}
          placeholder={props.dateFormatText}
          value={userInput}
          inputProps={{ readOnly: props.readonlyInput ? "readonly" : false }}
          format={DATE_FORMAT.DAY_PICKER_INPUT}
          formatDate={(date, format) =>
            date instanceof Date ? dateFnsFormat(date, format) : ""
          }
          parseDate={(str, format) => {
            setUserInput(str);
            const parsed = parse(str, format, new Date());

            return DateUtils.isDate(parsed) ? parsed : undefined;
          }}
          dayPickerProps={{
            disabledDays: [
              ...(props.disableSpecificDate ? [props.disableSpecificDate] : []),
              ...(props.disablePast ? [{ before: new Date() }] : []),
              ...(props.disableBeforeDate
                ? [{ before: props.disableBeforeDate }]
                : []),
              ...(props.disableWeekends ? [{ daysOfWeek: [0, 6] }] : []),
              ...(props.disableSundays ? [{ daysOfWeek: [0, 7] }] : []),
            ],
            selectedDays: selectedDay,
            month: pickerMonth,
            weekdaysShort: getWeekdaysShortByLang(props.lang),
            firstDayOfWeek: 1,
            captionElement: ({ date }: { date: Date }) =>
              renderCaptionElement({
                date,
                onChange: (date: Date) => setPickerMonth(date),
                disablePast: props.disablePast,
              }),
            navbarElement: Navbar,
          }}
          onDayChange={(day: Date) => {
            // @TODO: Refactor DayPicker output (props.onDayChange)
            // currently props.onDayChange is triggered twice - with Date and then with string (onDayPickerHide) as an output
            props.onDayChange(day);
            setSelectedDay(day);
            setDidModifyDay(true);
          }}
          onDayPickerHide={() => {
            if (selectedDay) {
              props.onDayChange(
                resolveOutputValue(selectedDay, props.returnRawDate),
              );
            }

            if (!selectedDay && didModifyDay) {
              props.onDayChange(null);
            }

            if (!userInput.match(DAY_PICKER_VALID_DATE_REGEX)) {
              props.onDayChange(
                resolveOutputValue(userInput, props.returnRawDate),
              );
            }

            if (pickerMonth) {
              setPickerMonth(undefined);
            }

            if (mobile || tablet) {
              forceBlur();
            }
          }}
          onDayPickerShow={() => {
            if (props.autoScroll) {
              scroller.scrollTo(DAY_PICKER_SCROLL_ELEMENT, {
                smooth: true,
                offset: SCROLL_ELEMENT_OFFSET,
              });
            }
          }}
        />
        {!props.hideIcon && <CalendarIcon size={24} variant="line" />}
        <FormikInputWithError
          isHidden
          name={props.inputName}
          defaultValue={props.defaultValue}
          handleChange={() => null}
          errorTestId={props.errorTestId}
        />
      </DayPickerInputWrapper>
    </TestWrapper>
  );
};

export { DayPicker };

const Navbar = (props: NavbarElementProps): React.ReactElement => {
  return (
    <div className={props.className}>
      <NavbarButton type="button" onClick={() => props.onPreviousClick()}>
        <CaretLeft size={24} height={40} />
      </NavbarButton>
      <NavbarButton type="button" onClick={() => props.onNextClick()}>
        <CaretRight size={24} height={40} />
      </NavbarButton>
    </div>
  );
};

const NavbarButton = styled.button`
  padding: 0 ${props => props.theme.margins.half};
  border: 0;
  background: transparent;
`;

const CalendarIcon = styled(CalendarDaily)`
  position: absolute;
  top: ${props => props.theme.margins.base_x2};
  right: ${props => props.theme.margins.base_x2};
  pointer-events: none;
`;

// Don't kill me.
// The only way to style react-day-picker plugin is to override its styles.
// And this plugin's stylesheet is full of ugly selectors ¯\_(ツ)_/¯
const DayPickerInputWrapper = styled.div<WrapperProps>`
  --calendarDay: ${props => props.theme.palette.grey20};

  ${({ variant, theme }) =>
    variant === DAY_PICKER_THEME_VARIANT.MAID &&
    css`
      --calendarDayActive: ${theme.palette.red300};
      --calendarDayBorder: ${theme.palette.red50};
      --calendarDayLight: ${theme.palette.red50};
    `}

  ${({ variant, theme }) =>
    variant === DAY_PICKER_THEME_VARIANT.CLIENT &&
    css`
      --calendarDayActive: ${theme.palette.blue500};
      --calendarDayBorder: ${theme.palette.blue50};
      --calendarDayLight: ${theme.palette.blue50};
    `}

  position: relative;

  .DayPickerInput {
    display: block;

    input {
      ${inputStyles}

      padding-top: ${props => props.theme.margins.base_x2};

      ${props =>
        props.isDisabled &&
        `
        background: ${props.theme.palette.grey20};
        pointer-events: none;
      `}
    }
  }

  .DayPicker-wrapper {
    width: ${dimensions.wrapperMobile};

    ${props => props.theme.breakpoints.tablet} {
      width: ${dimensions.wrapper};
    }
  }

  .DayPicker-NavBar {
    display: flex;
    justify-content: flex-end;
    padding-top: ${props => props.theme.margins.base_x2};
    padding-right: ${props => props.theme.margins.base};
  }

  .DayPickerInput-OverlayWrapper {
    z-index: 2;
  }

  .DayPicker-Month {
    margin-top: ${props => props.theme.margins.base_x2};
    border-collapse: separate;
  }

  .DayPicker-Weekday {
    color: ${props => props.theme.palette.grey700};
  }

  .DayPicker-Day {
    position: relative;
    width: ${dimensions.day};
    height: ${dimensions.day};
    font-size: ${props => props.theme.fonts.sizes.body2};

    ${props => props.theme.breakpoints.desktop} {
      font-size: ${props => props.theme.fonts.sizes.caption};
    }

    &:not(.DayPicker-Day--outside)::before {
      content: "";
      position: absolute;
      top: 50%;
      left: 50%;
      width: ${rem(32)};
      height: ${rem(32)};
      border: ${rem(1)} solid var(--calendarDay);
      border-radius: 50%;
      transform: translate(-50%, -50%);
      z-index: -1;
    }
  }

  .DayPicker-Day--selected:not(.DayPicker-Day--disabled):not(
      .DayPicker-Day--outside
    ) {
    background-color: transparent;
    color: ${props => props.theme.palette.white};

    &::before {
      background-color: var(--calendarDayActive);
      border: ${rem(1)} solid ${props => rgba(props.theme.palette.grey700, 0.2)};
    }

    &:hover {
      background-color: transparent;
      color: ${props => props.theme.palette.white};

      &::before {
        background-color: var(--calendarDayActive);
      }
    }
  }

  .DayPicker:not(.DayPicker--interactionDisabled)
    .DayPicker-Day:not(.DayPicker-Day--disabled):not(
      .DayPicker-Day--selected
    ):not(.DayPicker-Day--outside):hover {
    background-color: transparent;
    color: ${props => props.theme.palette.white};

    &::before {
      background-color: var(--calendarDayActive);
    }
  }

  .DayPicker-Day--today {
    color: var(--calendarDay);
  }

  .DayPicker-Day--today:not(.DayPicker-Day--disabled) {
    color: var(--calendarDayActive);
  }
`;

const DayPickerCaption = styled.div`
  display: block;
  position: absolute;
  top: ${props => props.theme.margins.base_x2};
  left: ${rem(16)};

  ${props => props.theme.breakpoints.tablet} {
    left: ${rem(24)};
  }
`;

const DropdownsWrapper = styled.div`
  display: flex;
  justify-content: flex-start;

  button {
    font-size: ${props => props.theme.fonts.sizes.body2};
    min-width: ${rem(80)};
  }
`;

const DayPickerLabel = styled.label`
  ${labelStyles}
  position: absolute;
  font-size: ${props => props.theme.fonts.sizes.caption};
  color: ${props => props.theme.palette.grey300};
  padding-left: ${props => props.theme.margins.base};
  padding-top: ${props => props.theme.margins.half};
  z-index: 1;
`;
